<script lang="ts">
import layout from '~/consumer/layouts/DefaultLayout.vue'
export default { layout }

</script>
<script setup lang="ts">import { ref as _ref } from 'vue';

import type { GiftIdeaLanding } from '@consumer/types/serializers'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import GiftlyAdvantage from '@consumer/components/GiftlyAdvantage.vue'
import GiftlyRating from '@consumer/components/GiftlyRating.vue'
import { replaceQuery } from '@shared/url'
import { groupBy } from '@consumer/helpers/object'
import { customizer } from '@consumer/api'
import { openCustomizerModal } from '@consumer/services/modals'
import giftIdeasApi from '@admin/api/Consumer/GiftIdeasApi'
import { useTrustpilot } from '@shared/composables/trustpilot'

useTrustpilot()

withDefaults(defineProps<{ currentTab: number; landing: GiftIdeaLanding }>(), {  })

// Track customizer modal opens (since modal doesn't hit server)
function trackCustomizerVisit () {
  const trackVisitUrl = customizer.trackVisit.path()
  fetch(trackVisitUrl, { method: 'GET', keepalive: true })
}

// Auto-open customizer in edit mode when ?editGiftIdea=<gift_idea_id> is present (used from admin).
onMounted(() => {
  const editId = new URLSearchParams(window.location.search).get('editGiftIdea')
  if (!editId) return

  const idea = __props.landing.ideas.find(i => i.id === Number(editId))
  if (!idea) return

  async function onSave (gift: GiftTemplate) {
    const { places, ...other } = gift
    const data = {
      ...other,
      places: places?.map(place => place.giftlyId) ?? [],
    }
    const newIdea = await giftIdeasApi.update({ params: idea!, data, visit: false })
    Object.assign(idea!, newIdea)
    await giftIdeasApi.updateGiftTags({ params: idea!, data: { giftTags: gift.giftTags } })
  }

  openCustomizerModal({ gift: idea, fromTools: true, onSave })
})

const ideasByCategory = computed(() => {
  const ideasByCategory = groupBy(__props.landing.ideas, 'category')

  // Add "Create My Own" at the beginning of each category.
  if (__props.landing.urlSlug === 'buy-a-gift') {
    Object.values(ideasByCategory).forEach(ideas => ideas.unshift(undefined as any))
  }

  // Add "Create My Own" at the end.
  if (__props.landing.nationwide || __props.landing.urlSlug === 'easter-gift-cards') {
    Object.values(ideasByCategory).forEach(ideas => ideas.push(undefined as any))
  }

  Object.values(ideasByCategory).forEach((ideas) => {
    // Drop the last idea if there are two missing elements to fill the row.
    if (ideas.length % 3 === 1 && ideas.length > 9) {
      ideas.splice(ideas.length - 1, 1)
    }
  })

  return ideasByCategory
})

const categories = computed(() => Object.keys(ideasByCategory.value))

let selectedTabIndex = _ref(__props.currentTab)

function onSelectTab (tabIndex: number) {
  replaceQuery({ tab: tabIndex })
  selectedTabIndex.value = tabIndex
}

function scrollToLearnMore () {
  const el = document.getElementById('giftly-advantage')

  el?.scrollIntoView({ behavior: 'smooth' })
}
</script>

<template>
  <div class="bg-white relative py-8 lg:py-12 font-medium" data-qa="GiftIdeaLanding">
    <Head>
      <title>{{ landing.title }}</title>
    </Head>

    <div
      class="text-center flex flex-col items-center max-w-70ch mx-auto px-4 text-balance"
      :class="categories.length <= 1 && 'mb-4'"
    >
      <h1
        class="text-3xl lg:text-4xl"
      >{{ landing.h1Copy }}</h1>

      <p
        class="text-base text-v2-tertiary mb-0 mt-3"
        v-html="landing.heroCopy"
      />
      <p class="text-base text-v2-tertiary pt-2">
        Satisfaction Guaranteed.
        <GButton flat label="Learn more" size="inherit" @click="scrollToLearnMore"/>
      </p>
    </div>

    <TabGroup
      as="div"
      class="lg:bg-surface-grey rounded-v2lg mx-auto lg:border border-grey-primary lg:my-10"
      :selectedIndex="selectedTabIndex"
      style="max-width: 1080px;"
      @change="onSelectTab"
    >
      <TabList
        v-show="categories.length > 1"
        class="w-max max-w-full mx-auto overflow-x-auto"
      >
        <div class="w-min flex gap-12 text-base font-semibold whitespace-nowrap justify-center px-4">
          <Tab
            v-for="category in categories"
            :key="category"
            v-slot="{selected}"
            class="pt-8 pb-6 text-v2-tertiary outline-none hover:text-primary-base"
            name="nc_lp_category_tab"
          >
            <span
              :class="selected && 'text-primary-base underline'"
              style="text-underline-offset: 12px;"
            >{{ category }}</span>
          </Tab>
        </div>
      </TabList>

      <TabPanels
        class="flex flex-col items-center mx-6 lg:mx-8"
      >
        <TabPanel
          v-for="ideas, category in ideasByCategory"
          :key="category"
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 justify-between justify-items-normal max-w-full
            outline-none py-8 border-t border-grey-primary"
        >
          <GiftlyIdea
            v-for="idea, j in ideas"
            :key="idea?.id || j"
            :idea="idea"
            name="nc_lp_card_select"
            data-qa="gift-idea"
            :onCardClick="trackCustomizerVisit"
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>

    <div
      class="grid lg:grid-cols-2 justify-center gap-12 p-8 mx-auto text-v2-secondary font-medium"
      style="max-width: 900px;"
    >
      <div id="giftly-advantage" class="max-w-60ch">
        <GiftlyAdvantage :howItWorks="landing.howItWorksCopy"/>
      </div>
      <div class="border-t border-grey-primary lg:border-none max-w-60ch">
        <GiftlyRating/>
      </div>
    </div>
  </div>
</template>
